import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  SectionPanel,
  StatusPill,
  CodeChip,
  ClinicalEmptyState,
  fmtMoney,
  tokens,
} from "../../shared/clinical-design";
import { CHARGE_STATUS, CONFIDENCE, VALIDATION_RESULT } from "../common/constants";
import * as svc from "../services/chargeCaptureService";
import CodeSearchAdd from "./CodeSearchAdd";
import ObservationsThread from "./ObservationsThread";

/**
 * Charge Slip Workspace (panel de detalle del master-detail, doc §UI/UX).
 * Muestra la nota clínica fuente, el charge slip recomendado por el engine
 * (N.1) con evidencia MEAT por código, las validaciones (N.2) y la acción
 * de push a AdvancedMD (N.4).
 */
export default function ChargeSlipPanel({ charge, loading, onChargeUpdated }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [pushing, setPushing] = useState(false);

  if (loading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton variant="rounded" height={90} />
        <Skeleton variant="rounded" height={220} />
        <Skeleton variant="rounded" height={160} />
      </Stack>
    );
  }

  if (!charge) {
    return (
      <SectionPanel sx={{ height: "100%" }}>
        <ClinicalEmptyState message="Select an encounter from the queue to review its recommended charge slip." sx={{ py: 8 }} />
      </SectionPanel>
    );
  }

  const status = CHARGE_STATUS[charge.status] || CHARGE_STATUS.DRAFT;
  const synced = charge.status === "SYNCED_AMD" || charge.status === "BILLED" || charge.status === "PAID";
  const readOnly = synced;

  const acceptedDx = charge.diagnoses.filter((d) => d.accepted);
  const acceptedCpt = charge.procedures.filter((p) => p.accepted);
  const hasFail = charge.validations.some((v) => v.result === "FAIL");
  const acceptedRaf = acceptedDx.reduce((acc, d) => acc + (d.rafContribution || 0), 0);

  const toggle = async (itemType, itemId, accepted) => {
    const updated = await svc.updateChargeDecision(charge.chargeId, { itemType, itemId, accepted });
    onChargeUpdated(updated);
  };

  const handlePush = async () => {
    setPushing(true);
    try {
      const updated = await svc.approveAndSync(charge.chargeId);
      onChargeUpdated(updated);
    } finally {
      setPushing(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      {/* Encabezado del encuentro */}
      <SectionPanel
        title={`Charge slip — ${charge.chargeId}`}
        actions={<StatusPill tone={status.tone} label={status.label} dense />}
      >
        <Stack direction="row" spacing={3} flexWrap="wrap" rowGap={0.5}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
            {charge.patientName}
            <Box component="span" sx={{ fontWeight: 400, color: tokens.ink3, ml: 0.8 }}>
              {charge.patientAge}y · {charge.patientGender}
            </Box>
          </Typography>
          <CodeChip code={charge.cipId} />
          <Typography sx={{ fontSize: 13, color: tokens.ink2 }}>
            DOS <Box component="span" sx={{ fontFamily: tokens.fontMono }}>{charge.dateOfService}</Box>
          </Typography>
          <Typography sx={{ fontSize: 13, color: tokens.ink2 }}>{charge.visitType}</Typography>
          <Typography sx={{ fontSize: 13, color: tokens.ink2 }}>{charge.providerName}</Typography>
          <Typography sx={{ fontSize: 13, color: tokens.ink2 }}>POS {charge.placeOfService}</Typography>
        </Stack>
        <Button
          size="small"
          onClick={() => setNoteOpen((o) => !o)}
          endIcon={noteOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: "none", color: tokens.primary, mt: 1, px: 0 }}
        >
          Source clinical note
        </Button>
        <Collapse in={noteOpen}>
          <Box
            sx={{
              mt: 0.5,
              p: 1.5,
              bgcolor: tokens.surface2,
              border: `1px solid ${tokens.line}`,
              borderRadius: "4px",
              maxHeight: 260,
              overflowY: "auto",
              whiteSpace: "pre-wrap",
              fontFamily: tokens.fontMono,
              fontSize: 12,
              color: tokens.ink2,
            }}
          >
            {charge.clinicalNote}
          </Box>
        </Collapse>
      </SectionPanel>

      {/* Diagnósticos recomendados + ingreso manual */}
      <SectionPanel title={`Diagnoses (${acceptedDx.length}/${charge.diagnoses.length} accepted)`}>
        {charge.diagnoses.map((d) => {
          const conf = d.confidence ? CONFIDENCE[d.confidence] : null;
          return (
            <Stack
              key={d.id}
              direction="row"
              spacing={1}
              alignItems="flex-start"
              sx={{
                py: 0.8,
                borderBottom: `1px solid ${tokens.line}`,
                "&:last-child": { borderBottom: 0 },
                opacity: d.accepted ? 1 : 0.55,
              }}
            >
              <Checkbox
                size="small"
                checked={d.accepted}
                disabled={readOnly}
                onChange={(e) => toggle("diagnosis", d.id, e.target.checked)}
                sx={{ p: 0.3, color: tokens.primary, "&.Mui-checked": { color: tokens.primary } }}
              />
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
                  <CodeChip code={d.icd10} />
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{d.description}</Typography>
                  {d.hccCode && <CodeChip code={d.hccCode} tone="warn" />}
                  {d.rafContribution > 0 && (
                    <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.ok }}>
                      +{d.rafContribution.toFixed(3)} RAF
                    </Typography>
                  )}
                  {conf ? (
                    <StatusPill tone={conf.tone} label={`${conf.label} confidence`} dense />
                  ) : (
                    <StatusPill tone="info" label="Manual entry" dense />
                  )}
                  {d.requiresQuery && <StatusPill tone="crit" label="Provider query suggested" dense />}
                  {d.codingOpportunityId && (
                    <Tooltip title="Closes a Coding Opportunity from Module E" arrow>
                      <span><CodeChip code={`OPP #${d.codingOpportunityId}`} tone="info" /></span>
                    </Tooltip>
                  )}
                </Stack>
                <Typography sx={{ fontSize: 12, color: tokens.ink3, mt: 0.3 }}>{d.meatEvidence}</Typography>
              </Box>
            </Stack>
          );
        })}
        {!readOnly && (
          <CodeSearchAdd
            placeholder="Add diagnosis — search ICD-10 by code or description…"
            lookup={svc.lookupDiagCodes}
            getCode={(r) => r.icd10}
            getExtra={(r) => (r.hccCode ? <CodeChip code={r.hccCode} tone="warn" /> : null)}
            onPick={async (row) => {
              const updated = await svc.addChargeDiagnosis(charge.chargeId, row);
              onChargeUpdated(updated);
            }}
          />
        )}
      </SectionPanel>

      {/* Procedimientos recomendados + ingreso manual */}
      <SectionPanel title={`Procedures (${acceptedCpt.length}/${charge.procedures.length} accepted)`}>
        {charge.procedures.map((p) => {
          const conf = p.confidence ? CONFIDENCE[p.confidence] : null;
          return (
            <Stack
              key={p.id}
              direction="row"
              spacing={1}
              alignItems="flex-start"
              sx={{
                py: 0.8,
                borderBottom: `1px solid ${tokens.line}`,
                "&:last-child": { borderBottom: 0 },
                opacity: p.accepted ? 1 : 0.55,
              }}
            >
              <Checkbox
                size="small"
                checked={p.accepted}
                disabled={readOnly}
                onChange={(e) => toggle("procedure", p.id, e.target.checked)}
                sx={{ p: 0.3, color: tokens.primary, "&.Mui-checked": { color: tokens.primary } }}
              />
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
                  <CodeChip code={p.cpt} tone="info" />
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{p.description}</Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.ink3 }}>
                    ×{p.units} {p.modifiers && `· mod ${p.modifiers}`} · dx {p.linkedIcd10}
                  </Typography>
                  {conf ? (
                    <StatusPill tone={conf.tone} label={`${conf.label} confidence`} dense />
                  ) : (
                    <StatusPill tone="info" label="Manual entry" dense />
                  )}
                  {p.careGapId && (
                    <Tooltip title="Closes an open Care Gap" arrow>
                      <span><CodeChip code={`GAP #${p.careGapId}`} tone="ok" /></span>
                    </Tooltip>
                  )}
                </Stack>
                <Typography sx={{ fontSize: 12, color: tokens.ink3, mt: 0.3 }}>{p.evidence}</Typography>
              </Box>
            </Stack>
          );
        })}
        {!readOnly && (
          <CodeSearchAdd
            placeholder="Add procedure — search CPT by code or description (lookupproccode)…"
            lookup={svc.lookupProcCodes}
            getCode={(r) => r.cpt}
            onPick={async (row) => {
              const updated = await svc.addChargeProcedure(charge.chargeId, row);
              onChargeUpdated(updated);
            }}
          />
        )}
      </SectionPanel>

      {/* Validaciones N.2 */}
      <SectionPanel title="Validation layer">
        {charge.validations.map((v) => {
          const res = VALIDATION_RESULT[v.result] || VALIDATION_RESULT.WARNING;
          return (
            <Stack key={v.id} direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.5 }}>
              <StatusPill tone={res.tone} label={res.label} dense sx={{ mt: 0.2, minWidth: 64, justifyContent: "center" }} />
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: tokens.ink2, letterSpacing: "0.03em" }}>
                  {v.type.replace(/_/g, " ")}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: tokens.ink2 }}>{v.message}</Typography>
              </Box>
            </Stack>
          );
        })}
      </SectionPanel>

      {/* Observaciones — diálogo coder ↔ médico */}
      <ObservationsThread charge={charge} onChargeUpdated={onChargeUpdated} />

      {/* Resumen + push */}
      <SectionPanel title="Summary & sync">
        <Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap" rowGap={1}>
          <Box>
            <Typography sx={{ fontSize: 11.5, color: tokens.ink3, textTransform: "uppercase" }}>Est. revenue</Typography>
            <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 18, fontWeight: 600 }}>
              {fmtMoney(charge.estimatedRevenue, { maximumFractionDigits: 2 })}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11.5, color: tokens.ink3, textTransform: "uppercase" }}>Accepted RAF</Typography>
            <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 18, fontWeight: 600, color: tokens.ok }}>
              +{acceptedRaf.toFixed(3)}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11.5, color: tokens.ink3, textTransform: "uppercase" }}>HCCs</Typography>
            <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 18, fontWeight: 600 }}>
              {acceptedDx.filter((d) => d.hccCode).length}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          {synced ? (
            <StatusPill
              tone="ok"
              label={`Synced to AdvancedMD ${charge.syncedAt ? new Date(charge.syncedAt).toLocaleString() : ""}`}
            />
          ) : (
            <Tooltip
              title={
                hasFail
                  ? "Resolve FAIL validations before pushing"
                  : acceptedCpt.length === 0
                  ? "Accept at least one procedure"
                  : "lookupproccode → savecharges (Tier 2) → getchargedetaildata"
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  disabled={pushing || hasFail || acceptedCpt.length === 0 || acceptedDx.length === 0}
                  onClick={handlePush}
                  sx={{ bgcolor: tokens.primary, "&:hover": { bgcolor: tokens.primary700 }, textTransform: "none", fontWeight: 600 }}
                >
                  {pushing ? "Pushing to AdvancedMD…" : "Approve & push to AdvancedMD"}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </SectionPanel>
    </Stack>
  );
}
